import {
  addToast,
  Button,
  Collapse,
  ColumnTable,
  Table,
  TextOutlinedInput,
} from '@octano/global-ui';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  StudyPlanVersion,
  StudyPlanVersionOffer,
} from '../../../../../types/studyPlan';
import {
  TariffPaymentMethodSettingValue,
  TuitionPaymentType,
} from '../../../../../types/tariffTypes';
import {
  checkCurrencyAsNumber,
  TariffFormatter,
} from '../../../../../utils/currency';
import { saveTariffRequest } from '../../api/tariff';
import CustomBadge from './TariffCustomBadge';

interface IForm {
  [key: string]: string;
}

interface ISubmitForm {
  offers: {
    tuitionTariff: string;
    tariff: string;
    credit: string;
    equivalenceCreditTariff: string;
  }[];
}

type TariffColumn = ColumnTable<Partial<StudyPlanVersionOffer>>;

type InputColumnConfig = {
  columnName: string;
  headerKey: string;
  field: keyof ISubmitForm['offers'][number];
  getValue: (offer: Partial<StudyPlanVersionOffer>) => number | undefined;
};

const printClassName = (
  index: number,
  data: Partial<StudyPlanVersionOffer>[],
  className?: string,
) => clsx(className, index === data.length - 1 && 'bg-white');

const checkValue = (value: string): number | null => {
  if (value === '') {
    return null;
  }
  return checkCurrencyAsNumber(value);
};

export default function TariffAccordion({
  row,
  fetchedConfigs,
  getStudyplanOffers,
}: {
  row: StudyPlanVersion;
  fetchedConfigs?: TariffPaymentMethodSettingValue | null;
  getStudyplanOffers: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { handleSubmit, control, formState } = useForm<IForm>({
    mode: 'onChange',
  });

  const hasCreditColumn =
    fetchedConfigs?.tuitionPaymentType ===
    TuitionPaymentType.TuitionContractCredits;
  const hasEquivalenceCreditColumn =
    fetchedConfigs?.hasEquivalenceCredit ?? false;

  const isFinalRow = useCallback(
    (data: Partial<StudyPlanVersionOffer>[] | undefined, index: number) =>
      (data?.length ?? 0) - 1 === index,
    [],
  );

  const onSubmit = useCallback(
    async (data: ISubmitForm) => {
      const formatedData = data.offers.map((inputs, offerId) => {
        const offer = row.offers.find((myOffer) => myOffer.id === offerId);
        return {
          tariffId: offer!.currentTariff?.id,
          tuitionTariff: checkValue(inputs.tuitionTariff),
          tariffValue: checkValue(inputs.tariff),
          creditValue: checkValue(inputs.credit),
          equivalenceCreditTariff: checkValue(inputs.equivalenceCreditTariff),
          academicOfferStudyPlanVersionOfferId: offer!
            .academicOfferStudyPlanVersionOfferId,
          studyPlanName: row.name,
          campusName: offer!.campus!.name,
          scheduleName: offer!.schedule!.name,
        };
      });

      const values = formatedData.filter(
        (value) => typeof value !== 'undefined',
      );

      if (values.length === 0) {
        return;
      }

      const result = await saveTariffRequest({ academicOfferTariffs: values });

      if (result.error && result.error.code === 'HTTP_ERROR') {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t('common.errors.save'),
        });
        return;
      }

      if (result.data) {
        addToast({
          icon: 'success',
          color: 'success',
          text: 'Cambios guardados exitosamente',
        });
        getStudyplanOffers();
      }
    },
    [row, getStudyplanOffers, t],
  );

  const buildInputColumn = useCallback(
    (config: InputColumnConfig): TariffColumn => ({
      columnName: config.columnName,
      headerText: t(config.headerKey),
      tdClassName: ({ index, data }) => printClassName(index, data!),
      cellFormat: ({ row: offer, index, data }) => {
        if (isFinalRow(data, index)) {
          return null;
        }
        return (
          <TextOutlinedInput
            textAlign="center"
            type="text"
            name={`offers[${offer.id}].${config.field}`}
            defaultValue={TariffFormatter(
              config.getValue(offer),
              offer.currentTariff?.currency,
            )}
            formatter={(value) =>
              TariffFormatter(value, offer.currentTariff?.currency)
            }
            control={control}
          />
        );
      },
    }),
    [t, control, isFinalRow],
  );

  const campusColumn = useMemo<TariffColumn>(
    () => ({
      columnName: 'campusName',
      bold: false,
      headerText: t('tariffAndTuition.tariff.table.campus'),
      width: '20%',
      tdClassName: ({ index, data }) => printClassName(index, data!),
      cellFormat: (options) => <p>{options.row.campus?.name}</p>,
    }),
    [t],
  );

  const scheduleColumn = useMemo<TariffColumn>(
    () => ({
      columnName: 'schedule',
      headerText: t('tariffAndTuition.tariff.table.schedule'),
      width: '20%',
      tdClassName: ({ index, data }) => printClassName(index, data!),
      cellFormat: (options) => <p>{options.row.schedule?.name}</p>,
    }),
    [t],
  );

  const tuitionColumn = useMemo<TariffColumn>(
    () =>
      buildInputColumn({
        columnName: 'newTuition',
        headerKey: 'tariffAndTuition.tariff.table.newTuition',
        field: 'tuitionTariff',
        getValue: (offer) => offer.currentTariff?.tuition,
      }),
    [buildInputColumn],
  );

  const tariffColumn = useMemo<TariffColumn>(
    () =>
      buildInputColumn({
        columnName: 'newTariff',
        headerKey: 'tariffAndTuition.tariff.table.newTariff',
        field: 'tariff',
        getValue: (offer) => offer.currentTariff?.value,
      }),
    [buildInputColumn],
  );

  const equivalenceCreditColumn = useMemo<TariffColumn>(
    () =>
      buildInputColumn({
        columnName: 'newEquivalenceCreditTariff',
        headerKey: 'tariffAndTuition.tariff.table.newEquivalenceCreditTariff',
        field: 'equivalenceCreditTariff',
        getValue: (offer) => offer.currentTariff?.equivalenceCreditTariff,
      }),
    [buildInputColumn],
  );

  const creditColumn = useMemo<TariffColumn>(
    () =>
      buildInputColumn({
        columnName: 'newCredit',
        headerKey: 'tariffAndTuition.tariff.table.newCredit',
        field: 'credit',
        getValue: (offer) => offer.currentTariff?.credit,
      }),
    [buildInputColumn],
  );

  const isOfferComplete = useCallback(
    (offer: Partial<StudyPlanVersionOffer>) => {
      const tariff = offer.currentTariff;
      if (typeof tariff?.tuition !== 'number') {
        return false;
      }
      if (typeof tariff?.value !== 'number') {
        return false;
      }
      if (hasCreditColumn && typeof tariff?.credit !== 'number') {
        return false;
      }
      return true;
    },
    [hasCreditColumn],
  );

  const statusColumn = useMemo<TariffColumn>(
    () => ({
      columnName: 'status',
      headerText: t('tariffAndTuition.tariff.table.status'),
      width: '20%',
      tdClassName: ({ index, data }) =>
        printClassName(index, data!, 'text-center'),
      cellFormat: ({ row: offer, index, data }) => {
        if (isFinalRow(data, index)) {
          return (
            <Button
              type="submit"
              text={t('tariffAndTuition.tariff.form.submitButtonAll')}
              size="sm"
              loading={formState.isSubmitting}
              fullwidth
            />
          );
        }
        return <CustomBadge value={isOfferComplete(offer) ? 1 : undefined} />;
      },
    }),
    [t, isFinalRow, isOfferComplete, formState.isSubmitting],
  );

  const renderColumns = useMemo<TariffColumn[]>(() => {
    const result: TariffColumn[] = [
      campusColumn,
      scheduleColumn,
      tuitionColumn,
      tariffColumn,
    ];
    if (hasEquivalenceCreditColumn) {
      result.push(equivalenceCreditColumn);
    }
    if (hasCreditColumn) {
      result.push(creditColumn);
    }
    result.push(statusColumn);
    return result;
  }, [
    campusColumn,
    scheduleColumn,
    tuitionColumn,
    tariffColumn,
    equivalenceCreditColumn,
    creditColumn,
    statusColumn,
    hasCreditColumn,
    hasEquivalenceCreditColumn,
  ]);

  const hasIncompleteOffer = useMemo(
    () => row.offers.some((offer) => !isOfferComplete(offer)),
    [row.offers, isOfferComplete],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} key={row.id}>
      <Collapse
        renderTitle={() => <p className="fs-14 text-dark fw-400">{row.name}</p>}
        renderBtn={({ isOpened, toggle }) => (
          <>
            <CustomBadge
              className={'ml-auto'}
              value={hasIncompleteOffer ? undefined : 1}
            />
            <Collapse.ToggleButton
              iconName={isOpened ? 'minus' : 'plus'}
              toggle={toggle}
              btnClassName="bg-primary ml-3"
              iconColor="white"
            />
          </>
        )}
      >
        <Table
          columns={renderColumns}
          data={[...row.offers, {}]}
          striped
          color="transparent"
          borderless={false}
        />
      </Collapse>
    </form>
  );
}
